import React, { ReactNode, useEffect, useState } from 'react';
import { equals } from 'ramda';
import { Label, LabelText } from '../../shared/BlockWidgets';
import { MultilinedText } from '../../shared/MultilinedText';
import { CommentButton } from '../../shared/Comment';
import { BaseCheckboxes } from '../BaseCheckboxes';
import * as styles from './Checkboxes.module.scss';
import {
  CheckboxesBlock,
  CheckboxesBlockValue,
} from 'concepts/Extraction/Blocks/Checkboxes/CheckboxesBlock';
import { Inline } from 'components/layout';
import { useDescribedBy } from 'hooks/useAccessibleName';
import { useDebouncedFunction } from 'hooks/useDebouncedFunction';

export type ReadonlyCheckboxesProps = {
  block: CheckboxesBlock;
  showSuggestion?: boolean;
  checkboxGroupDescribedBy?: string;
  otherTextFieldDescribedBy?: string;
  trailingContent?: ReactNode;
};

export type EditableCheckboxesProps = ReadonlyCheckboxesProps & {
  editable: true;
  value: CheckboxesBlockValue;
  comments: string | null;
  onChange: (newValue: CheckboxesBlockValue | null) => void;
  onChangeComments: (newComments: string) => void;
  onClickOption?: (optionId: string) => void;
  onOtherFieldFocus?: () => void;
  onOtherFieldClick?: () => void;
  hideClearSelection?: boolean;
};

export type CheckboxesProps = ReadonlyCheckboxesProps | EditableCheckboxesProps;

const isEditableCheckboxProps = (
  props: CheckboxesProps
): props is EditableCheckboxesProps =>
  !!(props as EditableCheckboxesProps).editable;

type RenderCommentsProps = {
  comments: EditableCheckboxesProps['comments'];
  blockLabel: CheckboxesBlock['label'];
  onChangeComments: EditableCheckboxesProps['onChangeComments'];
};

const renderCommentsButton = ({
  comments,
  blockLabel,
  onChangeComments,
}: RenderCommentsProps): JSX.Element => (
  <CommentButton
    comments={comments}
    blockLabel={blockLabel}
    onChangeComments={onChangeComments}
  />
);

export const Checkboxes = (props: CheckboxesProps): JSX.Element => {
  const { showSuggestion } = props;
  const [value, setValue] = useState((props as EditableCheckboxesProps).value);
  const [descriptionID, describedBy] = useDescribedBy();

  const isEditable = isEditableCheckboxProps(props);
  const noop = () => '';

  const editableValue = (props as EditableCheckboxesProps).value;
  useEffect(() => {
    setValue((current) =>
      equals(current, editableValue) ? current : editableValue
    );
  }, [editableValue]);

  const callback = isEditable
    ? (props as EditableCheckboxesProps).onChange
    : noop;

  const debouncedCallback = useDebouncedFunction(callback, 300);

  const ariaDescribedBy = [
    describedBy['aria-describedby'],
    props.checkboxGroupDescribedBy,
  ]
    .filter(Boolean)
    .join(' ');

  const checkboxesList = (
    <BaseCheckboxes
      aria-describedby={ariaDescribedBy || undefined}
      block={props.block}
      isDisabled={!isEditable}
      {...(isEditableCheckboxProps(props) && {
        value,
        onChange: (newValue: CheckboxesBlockValue | null) => {
          setValue(newValue as CheckboxesBlockValue);
          debouncedCallback(newValue);
        },
        canClearSelection: !props.hideClearSelection,
        onClickOption: props.onClickOption,
        onOtherOptionClick: props.onOtherFieldClick,
        onOtherOptionFocus: props.onOtherFieldFocus,
        otherTextFieldDescribedBy: props.otherTextFieldDescribedBy,
      })}
    />
  );

  const wrappedList = showSuggestion ? (
    <div className={styles.suggestionPending}>{checkboxesList}</div>
  ) : (
    checkboxesList
  );

  return (
    <>
      <Label>
        <div className={styles.labelOuter}>
          <div>
            <LabelText>{props.block.label}</LabelText>
            {props.block.description !== '' && (
              <div id={descriptionID} className={styles.description}>
                <MultilinedText>{props.block.description}</MultilinedText>
              </div>
            )}
          </div>
          {isEditableCheckboxProps(props) &&
            renderCommentsButton({
              comments: props.comments,
              blockLabel: props.block.label,
              onChangeComments: props.onChangeComments,
            })}
        </div>
      </Label>
      {props.trailingContent ? (
        <Inline spacing={2} alignY="top">
          {wrappedList}
          {props.trailingContent}
        </Inline>
      ) : (
        wrappedList
      )}
    </>
  );
};
